import { useState } from 'react';
import { Badge, Button, CloseButton, Group, NumberInput, Select, Stack, Text, Title } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import {
  useAddObligationAssignment,
  useObligationAssignments,
  useRemoveObligationAssignment,
  type ObligationAssignmentHost,
} from '../queries/obligation-assignments';
import { useObligationsList } from '../queries/obligations';
import { ApiError } from './api-error';
import type { Obligation, ObligationValueType } from '../types/api';

interface Props {
  host: ObligationAssignmentHost;
  /** The saved host record's id, or null for a brand-new/unsaved record. */
  id: string | null;
}

const HOST_LABEL: Record<ObligationAssignmentHost, string> = {
  lease: 'lease',
  contract: 'contract',
  well: 'well',
};

/** The `applies_to_<host>` column this host's candidates are filtered on
 * (mirrors api/src/lib/obligation-assignment-router.ts's `appliesToCol`). */
function appliesToColFor(host: ObligationAssignmentHost): keyof Obligation {
  switch (host) {
    case 'lease':
      return 'applies_to_lease';
    case 'contract':
      return 'applies_to_contract';
    case 'well':
      return 'applies_to_well';
  }
}

function formatValue(
  valueType: ObligationValueType,
  dateValue: string | null,
  daysValue: number | null,
): string {
  if (valueType === 'days') {
    return daysValue != null ? `${daysValue} day${daysValue === 1 ? '' : 's'}` : '—';
  }
  if (valueType === 'date') {
    return dateValue ?? '—';
  }
  return '—';
}

/**
 * Shared "Obligations" section for the lease/contract/well drawers (spec
 * docs/superpowers/specs/2026-08-16-land-data-obligations-design.md §4.2).
 * Modeled on CrossReferenceSection.tsx but value-aware: attaching an
 * obligation carries a per-attachment date or number-of-days value driven by
 * the CHOSEN obligation's `value_type` (validated server-side by
 * api/src/lib/obligation-assignment-router.ts's `assertValueMatchesType`).
 *
 * When `id` is null (a brand-new, unsaved host record) there is nothing to
 * attach yet — the section renders a muted note instead of the live
 * controls, same as CrossReferenceSection.
 */
export function ObligationAssignmentSection({ host, id }: Props) {
  const assignmentsQ = useObligationAssignments(host, id);
  const addM = useAddObligationAssignment(host, id);
  const removeM = useRemoveObligationAssignment(host, id);
  // Always call this (Rules of Hooks) — the same project-scoped list query
  // the ObligationListPage uses, so it shares the query cache.
  const obligationsQ = useObligationsList();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [dateValue, setDateValue] = useState<string | null>(null);
  const [daysValue, setDaysValue] = useState<number | null>(null);

  if (id == null) {
    return (
      <Stack gap="xs">
        <Title order={5}>Obligations</Title>
        <Text size="sm" c="dimmed">
          {`Save the ${HOST_LABEL[host]} first to add obligations.`}
        </Text>
      </Stack>
    );
  }

  const assigned = assignmentsQ.data ?? [];
  const assignedIds = new Set(assigned.map((a) => a.obligation_id));
  const appliesToCol = appliesToColFor(host);
  const candidates = (obligationsQ.data ?? []).filter(
    (o) => o[appliesToCol] === true && !assignedIds.has(o.id),
  );
  const selectedObligation = candidates.find((o) => o.id === selectedId) ?? null;

  function resetPicker() {
    setSelectedId(null);
    setDateValue(null);
    setDaysValue(null);
  }

  async function attach(obligationId: string, dv: string | null, days: number | null) {
    try {
      await addM.mutateAsync({ obligation_id: obligationId, date_value: dv, days_value: days });
      resetPicker();
    } catch (err) {
      notifications.show({
        title: 'Add failed',
        message: err instanceof ApiError ? err.message : 'Unexpected error',
        color: 'red',
      });
    }
  }

  async function handleSelect(obligationId: string) {
    setSelectedId(obligationId);
    setDateValue(null);
    setDaysValue(null);
    // A 'none' obligation carries no value — attach immediately rather than
    // making the user click through an empty value-input step. 'days'/'date'
    // obligations wait for the value input + explicit Attach click below.
    const obligation = candidates.find((o) => o.id === obligationId);
    if (obligation && obligation.value_type === 'none') {
      await attach(obligationId, null, null);
    }
  }

  async function handleRemove(assignmentId: string) {
    try {
      await removeM.mutateAsync(assignmentId);
    } catch (err) {
      notifications.show({
        title: 'Remove failed',
        message: err instanceof ApiError ? err.message : 'Unexpected error',
        color: 'red',
      });
    }
  }

  return (
    <Stack gap="md">
      <Title order={5}>Obligations</Title>

      <Stack gap={6}>
        {assigned.length === 0 && (
          <Text size="xs" c="dimmed">
            None attached
          </Text>
        )}
        {assigned.map((a) => (
          <Group key={a.id} gap="xs">
            <Badge
              variant="light"
              color="indigo"
              rightSection={
                <CloseButton
                  size="xs"
                  variant="transparent"
                  aria-label={`Remove ${a.name}`}
                  onClick={() => void handleRemove(a.id)}
                />
              }
            >
              {a.name}
            </Badge>
            <Text size="xs" c="dimmed">
              {formatValue(a.value_type, a.date_value, a.days_value)}
            </Text>
          </Group>
        ))}
      </Stack>

      <Select
        label="+ Add obligation"
        placeholder="Search…"
        data={candidates.map((o) => ({ value: o.id, label: o.name }))}
        value={selectedId}
        searchable
        clearable
        nothingFoundMessage="No candidates"
        onChange={(v) => {
          if (v) void handleSelect(v);
          else resetPicker();
        }}
      />

      {selectedObligation && selectedObligation.value_type === 'days' && (
        <Group align="flex-end" gap="xs">
          <NumberInput
            label="Value (days)"
            min={0}
            value={daysValue ?? ''}
            onChange={(v) => setDaysValue(typeof v === 'number' ? v : null)}
            w={160}
          />
          <Button
            size="xs"
            disabled={daysValue == null}
            onClick={() => void attach(selectedObligation.id, null, daysValue)}
          >
            Attach
          </Button>
        </Group>
      )}

      {selectedObligation && selectedObligation.value_type === 'date' && (
        <Group align="flex-end" gap="xs">
          <DateInput
            label="Value (date)"
            valueFormat="YYYY-MM-DD"
            value={dateValue}
            onChange={(v) => setDateValue(v ?? null)}
            clearable
            w={200}
          />
          <Button
            size="xs"
            disabled={dateValue == null}
            onClick={() => void attach(selectedObligation.id, dateValue, null)}
          >
            Attach
          </Button>
        </Group>
      )}
    </Stack>
  );
}
